import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {MODAL_FORM, MODAL_CONFIRM, MODAL_INFO, MODAL_WORKSPACE_MANAGER} from './cst'
import GraphData from './GraphData'

type ModalType = "modal_form" | "modal_confirm" | "modal_info" | "modal_workspace_manager"
type FieldValue = string | number; //Input types
type ModalData = Record<string, FieldValue> | //Output types. Form.
                 boolean | //Confirm
                 undefined //Info

interface FieldsDictionary {
  [fieldName: string]: FieldDescriptor;
}

interface FieldDescriptor { 
  type? : string
  value? : FieldValue
}

interface FieldData { //What the user inputs while calling modal
  fieldName: string;
  fieldValue?: FieldDescriptor; //Serves as either field type for form or value for info
}

interface ModalProps {
  type : ModalType
  title: string
  fieldsData: FieldData[]
  onSubmit: (formData: ModalData) => void;
}

export interface ModalUsageProps {
  clearHeldKeys : Function,
  setModalUp : Function,
  graphData : GraphData,
  reloadGraph : Function
}

const Modal: React.FC<ModalProps> = ({ type, title, fieldsData, onSubmit}) => {
  const [formData, setFormData] = useState<Record<string, FieldValue>>({});
  const formDataRef = useRef<Record<string, FieldValue>>({}); //Gotta return value from ref due to keyboard input
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const firstTextareaFieldRef = useRef<HTMLTextAreaElement | null>(null);
  let shiftDown = false

  useEffect(() => { //TODO proper defaults
    const initialFormData: Record<string, FieldValue> = {};
    fieldsData.forEach((field) => {
      initialFormData[field.fieldName] = '';
    });
    setFormData(initialFormData);
  }, [fieldsData]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);


  useEffect(() => { //Focus 1st element
    if(type===MODAL_FORM){ //Set defaults
      fieldsData.forEach(field => {
        const value = field.fieldValue.value
        if(value){
          setFormData((data) => {
            data[field.fieldName] = value
            return data
          })
        }
      })
    }

    if (firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
    if (firstTextareaFieldRef.current) {
      firstTextareaFieldRef.current.focus();
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const fieldName = event.target.name;
    const fieldValue = event.target.value;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: fieldValue,
    }));
  };


  const handleKeyPress = (event: KeyboardEvent) => {
    if(!shiftDown){
      if (event.key === 'Escape') {
        handleCancel()
      }
      if (event.key === 'Enter') {
        handleConfirm()
      }
    }
    if (event.key === 'Shift') {
      shiftDown = true
    }
  };
  const handleKeyRelease= (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      shiftDown = false
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    document.addEventListener('keyup', handleKeyRelease);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('keyup', handleKeyRelease);
    };
  }, []);

  const handleConfirm = () => {
    if(type===MODAL_FORM)
      onSubmit(formDataRef.current);
    else if(type===MODAL_CONFIRM)
      onSubmit(true)
    else
      onSubmit(undefined)

  };

  const handleCancel = () => {
    if(type===MODAL_FORM)
      onSubmit(undefined)
    else if(type===MODAL_CONFIRM)
      onSubmit(false)
    else
      onSubmit(undefined)
  }

  const capitalizeFirst = (str : string) : string=> {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  if(type===MODAL_FORM) return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-title">{title}</div>
        {fieldsData.map((field, index) => {
          if(field.fieldValue.type == "textarea"){
            return(
              <div key={field.fieldName} className="form-field">
                <label htmlFor={field.fieldName}>{capitalizeFirst(field.fieldName)}: </label>
                <textarea
                  id={field.fieldName}
                  name={field.fieldName}
                  value={formData[field.fieldName]}
                  onChange={handleInputChange}
                  ref={index === 0 ? firstTextareaFieldRef : null}
                />
              </div>

            )
          } else return(
          <div key={field.fieldName} className="form-field">
            <label htmlFor={field.fieldName}>{capitalizeFirst(field.fieldName)}: </label>
            <input
              type={field.fieldValue.type}
              id={field.fieldName}
              name={field.fieldName}
              value={formData[field.fieldName]}
              onChange={handleInputChange}
              ref={index === 0 ? firstFieldRef : null}
            />
          </div>
          )
        }
        )}
        <div className="modal-buttons">
          <button onClick={handleCancel}>Cancel</button>
          <button onClick={handleConfirm}>OK</button>
        </div>
      </div>
    </div>
  );

  if(type===MODAL_CONFIRM) return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-title">{title}</div>
        <div className="modal-buttons">
          <button onClick={handleCancel}>Cancel</button>
          <button onClick={handleConfirm}>OK</button>
        </div>
      </div>
    </div>
  );

  if(type===MODAL_INFO) return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-title">{title}</div>
        {fieldsData.map((field, index) => {
          if(field.fieldValue.type == "textarea"){
            return(
              <div key={field.fieldName} className="form-field">
                <label>{capitalizeFirst(field.fieldName)}: </label>
                <textarea disabled>{field.fieldValue.value}</textarea>
              </div>
            )
          } else return(
          <div key={field.fieldName} className="form-field">
            <label>{capitalizeFirst(field.fieldName)}: </label>
            <div className="modal-info-label">{field.fieldValue.value}</div>
          </div>
          )
        }
        )}
        <div className="modal-buttons">
          <button onClick={handleConfirm}>Close</button>
        </div>
      </div>
    </div>
  )

  if(type===MODAL_WORKSPACE_MANAGER){
    const handleAction = (a) => {
      onSubmit(a)
    }
    return (
      <div className="modal">
        <div className="modal-content">
          <div className="modal-title">{title}</div>
          {fieldsData.map((field, index) => {
            return(
            <div key={field.fieldName} className={index%2?"form-field":"form-field-dark"}>
              <label>{capitalizeFirst(field.fieldName)}</label>
              <div className="horizontal-button-group">
                <button onClick={()=>{handleAction({action:"edit", target:field.fieldValue.value})}}>Edit</button>
                <button onClick={()=>{handleAction({action:"delete", target:field.fieldValue.value})}}>Delete</button>
              </div>
            </div>
            )
          }
          )}
          <button style={{margin: "5px"}} onClick={()=>{handleAction({action:"create"})}}>New</button>
          <div className="modal-buttons">
            <button onClick={handleConfirm}>Close</button>
          </div>
        </div>
      </div>
    )

  } 

}

const modal = (type: ModalType, title: string, fields: FieldsDictionary = {}): Promise<ModalData> => {
  return new Promise<ModalData>((resolve, reject) => {
    if(document.querySelector(".modal")){ //Modal already up
        resolve(undefined);
        return
    }
    const fieldData: FieldData[] = Object.entries(fields).map(([fieldName, fieldValue]) => ({
      fieldName,
      fieldValue,
    }));

    const handleSubmit = (data: ModalData) => {
      resolve(data);
      closeModal()
    };

    const modalRoot = document.getElementById('modal-root');
    if (!modalRoot) {
      console.log('No modal root!!!');
      resolve(undefined);
      return;
    }

    const modalContainer = document.createElement('div');
    modalRoot.appendChild(modalContainer);

    const closeModal = () => {
      ReactDOM.unmountComponentAtNode(modalContainer);
      modalContainer.remove();
    };

    ReactDOM.render(
      <Modal type={type} title={title} fieldsData={fieldData} onSubmit={handleSubmit} />,
      modalContainer
    );
  });
};

export default modal;
